import { TechRadarApi } from '@backstage-community/plugin-tech-radar';
import { TechRadarLoaderResponse } from '@backstage-community/plugin-tech-radar-common';

// Shared quadrants and rings
const quadrants = [
  { id: '1', name: 'Automation Tools' },
  { id: '2', name: 'Automation Platform' },
  { id: '3', name: 'AI/ML Ops' },
  { id: '4', name: 'AI Models' },
];
const rings = [
  {
    id: 'adopt',
    name: 'ADOPT\n',
    color: '#81C784',
    description: 'Technologies proven and in widespread use across projects.',
  },
  {
    id: 'trial',
    name: 'TRIAL\n',
    color: '#64B5F6',
    description:
      'Technologies that are mature enough for use but still under evaluation.',
  },
  {
    id: 'assess',
    name: 'ASSESS\n',
    color: '#FFD54F',
    description: 'Emerging technologies worth exploring for future potential.',
  },
  {
    id: 'hold',
    name: 'HOLD\n',
    color: '#EF9A9A',
    description: 'Technologies on hold',
  },
];

// Team-specific data with correct key properties
const teamData: Record<string, { entries: any[] }> = {
  saasops: {
    entries: [
      // Automation Tools
      {
        id: 'python-tool',
        key: 'python-tool',
        title: 'Python',
        quadrant: '1',
        description:
          'General-purpose programming language with extensive libraries',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-15'),
            description:
              'Established as core programming language for automation and data tasks.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-10'),
            description: 'Successfully tested across multiple project types.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-11-15'),
            description:
              'Evaluated for ecosystem maturity and integration capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-08-20'),
            description:
              'Initially placed on hold during technology evaluation.',
          },
        ],
      },
      {
        id: 'selenium',
        key: 'selenium',
        title: 'Selenium',
        quadrant: '1',
        description:
          'Widely-used framework for automating web applications across multiple browsers.',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-03-15'),
            description:
              'Currently evaluating for our teams application testing needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-12-20'),
            description:
              'Previously on hold during testing tool assessment phase.',
          },
        ],
      },
      {
        id: 'rpa-tool',
        key: 'rpa-tool',
        title: 'RPA',
        quadrant: '1',
        description:
          'Robotic Process Automation (RPA) for automating repetitive, rule-based tasks.',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-05'),
            description: 'Adopted for general automation use.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-20'),
            description: 'Trial phase completed with positive outcomes.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-15'),
            description: 'Evaluated for automation capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-10'),
            description: 'Temporarily paused during evaluation phase.',
          },
        ],
      },
      {
        id: 'pandas',
        key: 'pandas',
        title: 'Pandas',
        quadrant: '1',
        description: 'Data analysis and manipulation library',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-25'),
            description: 'Established as primary data processing framework.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-10'),
            description: 'Successfully tested in data transformation projects.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-05'),
            description: 'Evaluated for performance and capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-15'),
            description: 'Initially on hold during data technology assessment.',
          },
        ],
      },
      {
        id: 'ollama-tool',
        key: 'ollama-tool',
        title: 'Ollama',
        quadrant: '1',
        description: 'Self-hosted AI model deployment platform',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-20'),
            description: 'Currently evaluating feasibilty.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-03-05'),
            description:
              'Initially on hold pending AI infrastructure strategy.',
          },
        ],
      },
      {
        id: 'paramiko',
        key: 'paramiko',
        title: 'Paramiko',
        quadrant: '1',
        description: 'Secure remote connection library',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-10'),
            description: 'Implemented for secure remote system operations.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-15'),
            description: 'Successfully tested in infrastructure workflows.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-20'),
            description: 'Evaluated for security and reliability.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-25'),
            description: 'Initially on hold during security assessment.',
          },
        ],
      },
      {
        id: 'numpy',
        key: 'numpy',
        title: 'NumPy',
        quadrant: '1',
        description: 'Scientific computing library',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-20'),
            description: 'Established as foundation for numerical processing.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-15'),
            description: 'Successfully tested in analytical applications.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-20'),
            description: 'Evaluated for performance optimization.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-25'),
            description: 'Initially on hold during technology assessment.',
          },
        ],
      },

      // Automation Platform
      {
        id: 'uipath',
        key: 'uipath',
        title: 'UiPath',
        quadrant: '2',
        description: 'End-to-end automation platform',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-25'),
            description: 'Implemented as strategic automation platform.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-15'),
            description: 'Successfully tested in enterprise scenarios.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-11-20'),
            description: 'Evaluated against industry alternatives.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-08-25'),
            description: 'Initially on hold during platform assessment.',
          },
        ],
      },
      {
        id: 'vsts',
        key: 'vsts',
        title: 'VSTS',
        quadrant: '2',
        description: 'Integrated development platform',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-10'),
            description: 'Established as core development operations platform.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-20'),
            description: 'Successfully tested across various scenarios.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-15'),
            description: 'Evaluated for integration capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-20'),
            description: 'Initially on hold during platform evaluation.',
          },
        ],
      },
      {
        id: 'gocd',
        key: 'gocd',
        title: 'GO CD',
        quadrant: '2',
        description: 'Deployment pipeline automation',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Implemented for reliable software delivery.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-25'),
            description: 'Successfully tested in deployment scenarios.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-20'),
            description: 'Evaluated for pipeline flexibility.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-15'),
            description: 'Initially on hold during delivery strategy planning.',
          },
        ],
      },
      {
        id: 'fastapi-platform',
        key: 'fastapi-platform',
        title: 'FastAPI',
        quadrant: '2',
        description: 'Modern API development framework',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-15'),
            description: 'Established as preferred API development solution.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-20'),
            description: 'Successfully tested across service endpoints.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-25'),
            description: 'Evaluated for speed and developer experience.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-20'),
            description: 'Initially on hold during framework assessment.',
          },
        ],
      },
      {
        id: 'sonarqube',
        key: 'sonarqube',
        title: 'Sonar Qube',
        quadrant: '2',
        description: 'Code quality inspection platform',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-02-15'),
            description: 'Implemented as the quality management solution.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-11-20'),
            description: 'Successfully tested across codebase.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-08-25'),
            description: 'Evaluated for quality metrics and insights.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-05-20'),
            description: 'Initially on hold during quality tools assessment.',
          },
        ],
      },
      {
        id: 'pytest',
        key: 'pytest',
        title: 'PyTest',
        quadrant: '2',
        description: 'Python testing framework',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-05'),
            description: 'Established as standard testing framework.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-10'),
            description: 'Successfully tested across multiple projects.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-15'),
            description: 'Evaluated for testing productivity and coverage.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-20'),
            description:
              'Initially on hold during testing strategy development.',
          },
        ],
      },
      {
        id: 'postgres-db',
        key: 'postgres-db',
        title: 'Postgres',
        quadrant: '2',
        description: 'Open-source database system',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-02'),
            description: 'Implemented as primary relational database solution.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-10'),
            description: 'Successfully tested for data storage requirements.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-01-15'),
            description: 'Evaluated for reliability and performance.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-10-20'),
            description: 'Initially on hold during database strategy planning.',
          },
        ],
      },
      {
        id: 'playwright',
        key: 'playwright',
        title: 'Playwright',
        quadrant: '2',
        description: 'End-to-end testing framework',
        url: '#',
        timeline: [
          {
            ringId: 'trial',
            moved: 0,
            date: new Date('2025-03-25'),
            description: 'Currently testing for modern web application needs.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-12-15'),
            description: 'Evaluated as alternative testing approach.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-09-20'),
            description: 'Initially on hold during test automation assessment.',
          },
        ],
      },
      {
        id: 'datahub',
        key: 'datahub',
        title: 'DataHub',
        quadrant: '2',
        description: 'Data discovery and governance platform',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Currently evaluating for data management needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-20'),
            description:
              'Initially on hold during governance strategy planning.',
          },
        ],
      },

      // AI/ML Ops
      {
        id: 'sharepoint',
        key: 'sharepoint',
        title: 'Sharepoint',
        quadrant: '3',
        description: 'Document management and collaboration',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-02-20'),
            description:
              'Implemented as enterprise knowledge sharing platform.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-11-15'),
            description: 'Successfully tested in multiple teams.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-08-20'),
            description: 'Evaluated for team collaboration features.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-05-15'),
            description:
              'Initially on hold during collaboration tools assessment.',
          },
        ],
      },
      {
        id: 'python-mlops',
        key: 'python-mlops',
        title: 'Python',
        quadrant: '3',
        description: 'ML workflow scripting',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-15'),
            description: 'Established as core ML implementation language.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-20'),
            description: 'Successfully tested in ML automation workflows.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-25'),
            description: 'Evaluated for ML ecosystem compatibility.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-15'),
            description: 'Initially on hold during ML strategy development.',
          },
        ],
      },
      {
        id: 'aws-lambda',
        key: 'aws-lambda',
        title: 'AWS Lambda',
        quadrant: '3',
        description: 'Event-driven computing service',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-01'),
            description: 'Implemented for serverless application components.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-10'),
            description: 'Successfully tested for lightweight services.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-15'),
            description: 'Evaluated for efficiency and scalability.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-20'),
            description: 'Initially on hold during cloud strategy planning.',
          },
        ],
      },
      {
        id: 's3-bucket',
        key: 's3-bucket',
        title: 'S3 Bucket',
        quadrant: '3',
        description: 'Cloud storage solution',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-15'),
            description: 'Implemented for scalable asset storage.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-12-20'),
            description: 'Successfully tested for content management.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-09-25'),
            description: 'Evaluated for reliability and integration features.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-06-15'),
            description: 'Initially on hold during storage strategy planning.',
          },
        ],
      },
      {
        id: 'vscode',
        key: 'vscode',
        title: 'VS Code',
        quadrant: '3',
        description: 'Code editor with extensions',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-02-05'),
            description: 'Established as primary development environment.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2024-11-10'),
            description: 'Successfully tested across teams.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-08-15'),
            description: 'Evaluated for productivity and extension ecosystem.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-05-20'),
            description:
              'Initially on hold during development tools assessment.',
          },
        ],
      },
      {
        id: 'fastapi-mlops',
        key: 'fastapi-mlops',
        title: 'FastAPI',
        quadrant: '3',
        description: 'High-performance API framework',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-25'),
            description: 'Implemented for service endpoint development.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-01-05'),
            description: 'Successfully tested in API services.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-10-10'),
            description: 'Evaluated for performance and documentation.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-07-15'),
            description: 'Initially on hold during framework assessment.',
          },
        ],
      },
      {
        id: 'kubeflow',
        key: 'kubeflow',
        title: 'KubeFlow - Pipelines',
        quadrant: '3',
        description: 'ML workflow orchestration',
        url: '#',
        timeline: [
          {
            ringId: 'trial',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Currently testing for workflow automation.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-02-25'),
            description: 'Evaluated for orchestration capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-05'),
            description: 'Initially on hold during platform assessment.',
          },
        ],
      },
      {
        id: 'mlflow',
        key: 'mlflow',
        title: 'MLFlow',
        quadrant: '3',
        description: 'ML lifecycle management',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-20'),
            description: 'Currently evaluating for ML lifecycle needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-03-01'),
            description: 'Initially on hold during tooling assessment.',
          },
        ],
      },
      {
        id: 'faiss',
        key: 'faiss',
        title: 'Vector DB - FAISS',
        quadrant: '3',
        description: 'Similarity search library',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-10'),
            description: 'Currently evaluating for similarity search needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-25'),
            description: 'Initially on hold during database assessment.',
          },
        ],
      },
      {
        id: 'milvus',
        key: 'milvus',
        title: 'Vector DB - MILVUS',
        quadrant: '3',
        description: 'Vector database platform',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Currently evaluating for vector storage needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-02-01'),
            description: 'Initially on hold during database assessment.',
          },
        ],
      },
      {
        id: 'langchain',
        key: 'langchain',
        title: 'LangChain',
        quadrant: '3',
        description: 'AI application framework',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-25'),
            description: 'Currently evaluating for AI application development.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-02-15'),
            description: 'Initially on hold during AI strategy planning.',
          },
        ],
      },
      {
        id: 'bedrock',
        key: 'bedrock',
        title: 'Bedrock',
        quadrant: '3',
        description: 'AI service integration',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-05-01'),
            description: 'Currently evaluating for AI service needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-02-20'),
            description: 'Initially on hold during AI service assessment.',
          },
        ],
      },
      {
        id: 'postgres-server',
        key: 'postgres-server',
        title: 'Postgres Server',
        quadrant: '3',
        description: 'Database server solution',
        url: '#',
        timeline: [
          {
            ringId: 'assess',
            moved: 0,
            date: new Date('2025-04-05'),
            description: 'Currently evaluating for data storage needs.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-15'),
            description: 'Initially on hold during database strategy planning.',
          },
        ],
      },
      {
        id: 'gcp-agent-space',
        key: 'gcp-agent-space',
        title: 'GCP Agent Space',
        quadrant: '3',
        description: 'AI agent development environment',
        url: '#',
        timeline: [
          {
            ringId: 'trial',
            moved: 0,
            date: new Date('2025-04-30'),
            description: 'Currently testing for conversational AI development.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-03-15'),
            description: 'Evaluated for virtual assistant capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-30'),
            description: 'Initially on hold during AI platform assessment.',
          },
        ],
      },
      {
        id: 'moogsoft',
        key: 'moogsoft',
        title: 'MoogSoft',
        quadrant: '3',
        description: 'IT operations intelligence',
        url: '#',
        timeline: [
          {
            ringId: 'hold',
            moved: 0,
            date: new Date('2025-02-10'),
            description: 'Currently on hold pending monitoring strategy.',
          },
        ],
      },

      // AI Models
      {
        id: 'ollama-model',
        key: 'ollama-model',
        title: 'Ollama',
        quadrant: '4',
        description: 'Local AI model management',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Implemented for local AI deployment.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-03-01'),
            description: 'Successfully tested for various solutions.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-01-15'),
            description: 'Evaluated for local AI capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-12-01'),
            description: 'Initially on hold during AI deployment planning.',
          },
        ],
      },
      {
        id: 'baai-embedding',
        key: 'baai-embedding',
        title: 'Embedding Models – BAAI/BGE-LARGE1.5',
        quadrant: '4',
        description: 'Semantic representation models',
        url: '#',
        timeline: [
          {
            ringId: 'trial',
            moved: 0,
            date: new Date('2025-04-20'),
            description: 'Currently testing for semantic search applications.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-03-05'),
            description: 'Evaluated for embedding quality and performance.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-20'),
            description: 'Initially on hold during model evaluation.',
          },
        ],
      },
      {
        id: 'reranking-mini-lm',
        key: 'reranking-mini-lm',
        title: 'Reranking Models – CrossEncoder-Mini LM',
        quadrant: '4',
        description: 'Search result enhancement',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-10'),
            description: 'Implemented for search result refinement.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-25'),
            description: 'Successfully tested in information retrieval.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-01-15'),
            description: 'Evaluated for search quality improvement.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-12-05'),
            description: 'Initially on hold during search strategy planning.',
          },
        ],
      },
      {
        id: 'python-retrieval',
        key: 'python-retrieval',
        title: 'Python Retrieval Scripts',
        quadrant: '4',
        description: 'Information retrieval tools',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-03-20'),
            description: 'Fully integrated into production systems.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-10'),
            description: 'Expanded testing in various use cases.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-01-02'),
            description: 'Initial evaluation of capabilities.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-12-10'),
            description: 'Preliminary review phase.',
          },
        ],
      },
      {
        id: 'gemma3',
        key: 'gemma3',
        title: 'Gemma 3 4k 12b & 27b',
        quadrant: '4',
        description: 'Open-source LLMs',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-05-02'),
            description: 'Full production implementation.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-03-05'),
            description: 'Expanded testing across teams.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2025-02-11'),
            description: 'Technical evaluation phase.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2025-01-02'),
            description: 'Initial consideration period.',
          },
        ],
      },
      {
        id: 'llama3',
        key: 'llama3',
        title: 'Llama 3.1 7b 4k',
        quadrant: '4',
        description: 'Open-source LLM',
        url: '#',
        timeline: [
          {
            ringId: 'adopt',
            moved: 0,
            date: new Date('2025-04-15'),
            description: 'Deployed to production environment.',
          },
          {
            ringId: 'trial',
            moved: 1,
            date: new Date('2025-02-10'),
            description: 'Extended pilot testing.',
          },
          {
            ringId: 'assess',
            moved: 1,
            date: new Date('2024-12-05'),
            description: 'Capability assessment completed.',
          },
          {
            ringId: 'hold',
            moved: 1,
            date: new Date('2024-11-03'),
            description: 'Initial technology review.',
          },
        ],
      },
    ],
  },
  // backend: {
  //   entries: [
  //     {
  //       id: 'nodejs',
  //       key: 'nodejs',
  //       title: 'Node.js',
  //       quadrant: '2',
  //       description: 'JavaScript runtime for backend services.',
  //       url: '#',
  //       timeline: [
  //         {
  //           ringId: 'adopt',
  //           date: new Date('2024-04-10'),
  //           description: 'Commonly used in backend APIs.',
  //         },
  //       ],
  //     },
  //     {
  //       id: 'postgresql',
  //       key: 'postgresql',
  //       title: 'PostgreSQL',
  //       quadrant: '4',
  //       description: 'Robust relational database system.',
  //       url: '#',
  //       timeline: [
  //         {
  //           ringId: 'adopt',
  //           date: new Date('2024-02-01'),
  //           description: 'Main backend database choice.',
  //         },
  //       ],
  //     },
  //   ],
  // },
  // devops: {
  //   entries: [
  //     {
  //       id: 'git',
  //       key: 'git',
  //       title: 'Git',
  //       quadrant: '1',
  //       description: 'Version control system.',
  //       url: '#',
  //       timeline: [
  //         {
  //           ringId: 'adopt',
  //           date: new Date('2024-01-01'),
  //           description: 'Used across all teams.',
  //         },
  //       ],
  //     },
  //   ],
  // },
};

export class MyOwnClient implements TechRadarApi {
  async load(teamId?: string): Promise<TechRadarLoaderResponse> {
    const teamKey =
      (teamId?.toLowerCase() as keyof typeof teamData) || 'saasops';
    return {
      entries: teamData[teamKey].entries,
      quadrants,
      rings,
    };
  }
}
